import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from '@xstate/store/react';
import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useApiClient } from '@eluve/api-client-provider';
import { LanguageSelectorComboBox } from '@eluve/blocks';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Icon,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Separator,
  SimpleTooltip,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  Textarea,
  useToast,
} from '@eluve/components';
import { DEFAULT_LANGUAGE } from '@eluve/language-utils';
import {
  DynamicArtifactTemplate,
  hydrateDynamicArtifactTemplate,
  hydrateDynamicArtifactTemplatePreview,
} from '@eluve/llm-outputs';
import { useUserIdFromSession } from '@eluve/session-helpers';
import {
  DynamicArtifactRenderer,
  PromptTemplateSelector,
  templateBuilderStore,
  useUserSpecialty,
} from '@eluve/smart-blocks';
import { SampleTranscriptsBySpecialty } from '@eluve/utils';

const formSchema = z.object({
  promptTemplateId: z.string().min(1, 'Prompt template is required'),
  appointmentId: z
    .string()
    .optional()
    .refine((val) => !val || z.string().uuid().safeParse(val).success, {
      message: 'Must be a valid UUID',
    }),
  transcript: z.string().optional(),
  outputLanguageCode: z.string().min(1, 'Output language is required'),
});

type FormValues = z.infer<typeof formSchema>;

interface DynamicArtifactPreviewProps {
  dynamicArtifact?: DynamicArtifactTemplate;
}

export const DynamicArtifactPreview: FC<DynamicArtifactPreviewProps> = ({
  dynamicArtifact,
}) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [inputMethod, setInputMethod] = useState<'transcript' | 'appt-id'>(
    'transcript',
  );
  const [generatedOutput, setGeneratedOutput] = useState<
    DynamicArtifactTemplate | undefined
  >(undefined);
  const apiClient = useApiClient();
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      promptTemplateId: '',
      appointmentId: '',
      transcript: '',
      outputLanguageCode: DEFAULT_LANGUAGE,
    },
    mode: 'onChange',
  });

  const userId = useUserIdFromSession();
  const { specialty } = useUserSpecialty(userId);

  const promptTemplateId = form.watch('promptTemplateId');
  const appointmentId = form.watch('appointmentId');
  const transcript = form.watch('transcript');
  const outputLanguageCode = form.watch('outputLanguageCode');
  const isValid =
    Boolean(promptTemplateId?.trim()) &&
    ((inputMethod === 'appt-id' && Boolean(appointmentId?.trim())) ||
      (inputMethod === 'transcript' && Boolean(transcript?.trim())));

  const onSubmit = async (values: FormValues) => {
    if (!dynamicArtifact) {
      toast.error('No template available for generation');
      return;
    }

    setIsSubmitting(true);

    try {
      const summary = await apiClient.adminLlm.summarizeWithOutputTemplate({
        body: {
          transcript: values.transcript,
          appointmentId: values.appointmentId,
          promptTemplateId: values.promptTemplateId,
          outputTemplate: dynamicArtifact,
          outputLanguage: values.outputLanguageCode,
        },
      });

      if (summary.status === 201) {
        const { output } = summary.body;
        const hydrated = hydrateDynamicArtifactTemplate(
          dynamicArtifact,
          (output as Record<string, unknown>) ?? {},
        );
        setGeneratedOutput(hydrated);
        setShowForm(false);
      } else if (summary.status === 422) {
        const errorMessage = summary.body.message ?? 'Failed to generate note';
        toast.error(errorMessage);
      }
    } catch (error) {
      toast.error('Failed to generate note', {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack gap={4}>
      <Form {...form}>
        <form className="w-full" onSubmit={form.handleSubmit(onSubmit)}>
          <Stack
            className="rounded-lg bg-bg-primary p-5"
            gap={5}
            align="stretch"
          >
            {showForm && (
              <>
                <Text weight="regular" color="secondary">
                  To generate a note, provide a transcript by:
                </Text>
                <RadioGroup
                  defaultValue="transcript"
                  value={inputMethod}
                  onValueChange={(value: 'transcript' | 'appt-id') => {
                    setInputMethod(value);
                    // Clear the opposite field when switching methods
                    if (value === 'transcript') {
                      form.setValue('appointmentId', '');
                    } else {
                      form.setValue('transcript', '');
                    }
                  }}
                  className="flex gap-4"
                >
                  <Stack gap={2} align="start">
                    <Label className="flex cursor-pointer gap-3">
                      <RadioGroupItem id="transcript" value="transcript" />
                      Choosing a sample transcript
                    </Label>

                    <Label className="flex cursor-pointer gap-3">
                      <RadioGroupItem id="appt-id" value="appt-id" />
                      Providing an appointment ID
                    </Label>
                  </Stack>
                </RadioGroup>

                {inputMethod === 'appt-id' && (
                  <FormField
                    control={form.control}
                    name="appointmentId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Appointment ID</FormLabel>
                        <FormControl>
                          <Input disabled={isSubmitting} {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {inputMethod === 'transcript' && (
                  <Stack gap={2}>
                    <FormField
                      control={form.control}
                      name="transcript"
                      render={({ field }) => (
                        <FormItem className="w-full">
                          <FormLabel>Transcript</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Paste a sample transcript"
                              disabled={isSubmitting}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Stack gap={2}>
                      <Text size="xs" className="text-nowrap">
                        Or choose a sample transcript:
                      </Text>
                      <Stack gap={2} direction="horizontal" wrap>
                        {specialty &&
                          SampleTranscriptsBySpecialty[specialty] && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                form.setValue(
                                  'transcript',
                                  SampleTranscriptsBySpecialty[specialty],
                                )
                              }
                              disabled={isSubmitting}
                            >
                              {specialty}
                            </Button>
                          )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            form.setValue(
                              'transcript',
                              SampleTranscriptsBySpecialty['General Practice'],
                            )
                          }
                          disabled={isSubmitting}
                        >
                          General Practice
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            form.setValue(
                              'transcript',
                              SampleTranscriptsBySpecialty['Cardiology'],
                            )
                          }
                          disabled={isSubmitting}
                        >
                          Cardiology
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                )}

                <Separator />

                <FormField
                  control={form.control}
                  name="promptTemplateId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Prompt Template</FormLabel>
                      <FormControl>
                        <PromptTemplateSelector
                          value={field.value}
                          onTemplateSelected={field.onChange}
                          disabled={isSubmitting}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="outputLanguageCode"
                  render={({ field }) => (
                    <FormItem className="mb-4">
                      <FormLabel>Output Language</FormLabel>
                      <FormControl>
                        <LanguageSelectorComboBox
                          disabled={isSubmitting}
                          ariaLabelPrefix="Output Language"
                          selectButtonStyles="h-10 w-full rounded-md border border-border-primary bg-bg-primary px-3 py-2 text-sm"
                          selectedLanguageCode={outputLanguageCode}
                          onSelectedLanguageCode={async (
                            languageCode: string,
                          ) => {
                            field.onChange(languageCode);
                          }}
                          showLanguageIcon={false}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}
            <Stack gap={2} direction="horizontal">
              <Stack className="flex-1" align="stretch" gap={2}>
                <Button
                  disabled={!isValid}
                  loading={isSubmitting}
                  type="submit"
                >
                  {generatedOutput ? 'Re-generate Note' : 'Generate Note'}
                </Button>
              </Stack>
              {generatedOutput && (
                <SimpleTooltip
                  label={showForm ? 'Hide details' : 'Show details'}
                >
                  <Button
                    variant="outline"
                    onClick={() => setShowForm(!showForm)}
                  >
                    <Icon
                      name={showForm ? 'ChevronUp' : 'ChevronDown'}
                      size="auto"
                    />
                  </Button>
                </SimpleTooltip>
              )}
            </Stack>
          </Stack>
        </form>
      </Form>
      {generatedOutput && (
        <Stack className="rounded-lg bg-bg-primary p-5" gap={2}>
          <DynamicArtifactRenderer blocks={generatedOutput.blocks} isReadonly />
        </Stack>
      )}
    </Stack>
  );
};

export const DynamicArtifactTemplatePreview: FC = () => {
  const [tab, setTab] = useState('RENDERER');

  const name = useSelector(templateBuilderStore, (state) => state.context.name);
  const description = useSelector(
    templateBuilderStore,
    (state) => state.context.description,
  );
  const blocks = useSelector(
    templateBuilderStore,
    (state) => state.context.blocks,
  );

  const dynamicArtifact: DynamicArtifactTemplate = {
    name,
    description,
    blocks,
  };
  const hydratedTemplate =
    hydrateDynamicArtifactTemplatePreview(dynamicArtifact);

  return (
    <Tabs className="w-full place-items-center" value={tab}>
      <TabsList className="mb-4 grid w-fit grid-cols-2">
        <TabsTrigger
          value="RENDERER"
          onClick={() => setTab('RENDERER')}
          className="px-4"
        >
          Preview Template
        </TabsTrigger>
        <TabsTrigger
          value="PREVIEW"
          onClick={() => setTab('PREVIEW')}
          className="px-4"
        >
          Generate Note
        </TabsTrigger>
      </TabsList>
      <TabsContent forceMount value="RENDERER" className="w-full">
        <Stack className="rounded-lg bg-bg-primary p-5" gap={2}>
          <DynamicArtifactRenderer
            blocks={hydratedTemplate.blocks ?? []}
            isReadonly
          />
        </Stack>
      </TabsContent>
      <TabsContent forceMount value="PREVIEW" className="w-full">
        <DynamicArtifactPreview dynamicArtifact={dynamicArtifact} />
      </TabsContent>
    </Tabs>
  );
};
